import React from 'react';
import {
  Radio as MuiRadio,
  RadioGroup as MuiRadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Box,
  styled,
} from '@mui/material';
import type { RadioProps, RadioGroupProps } from '@mui/material';
import { alpha } from '@mui/material/styles';

// Option interface
export interface RadioOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: React.ReactNode;
}

// Custom radio variants
export type RadioVariant = 'default' | 'button' | 'card';
export type RadioSize = 'small' | 'medium' | 'large';

// Extended props interface
export interface CustomRadioGroupProps extends Omit<RadioGroupProps, 'size'> {
  variant?: RadioVariant;
  size?: RadioSize;
  options: RadioOption[];
  label?: string;
  description?: string;
  error?: boolean;
  helperText?: string;
  orientation?: 'horizontal' | 'vertical';
}

// Individual radio props
export interface CustomRadioProps extends Omit<RadioProps, 'size'> {
  variant?: RadioVariant;
  size?: RadioSize;
  label?: string;
  description?: string;
}

// Styled components
const StyledRadio = styled(MuiRadio)<{
  variant?: RadioVariant;
  size?: RadioSize;
  error?: boolean;
}>(({ theme, variant: radioVariant, size, error }) => {
  const sizeConfig = {
    small: {
      size: 16,
      fontSize: '0.75rem',
    },
    medium: {
      size: 20,
      fontSize: '0.875rem',
    },
    large: {
      size: 24,
      fontSize: '1rem',
    },
  };

  const currentSize = sizeConfig[size || 'medium'];
  const color = error ? theme.palette.error.main : theme.palette.primary.main;

  const baseStyles = {
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(color, 0.04),
    },
    '&.Mui-checked': {
      color: color,
    },
    '& .MuiSvgIcon-root': {
      fontSize: currentSize.size,
    },
  };

  const variantStyles = {
    default: {},
    button: {
      borderRadius: theme.spacing(1),
      border: `1px solid ${theme.palette.grey[300]}`,
      margin: theme.spacing(0.25),
      '&:hover': {
        borderColor: color,
        backgroundColor: alpha(color, 0.04),
      },
      '&.Mui-checked': {
        borderColor: color,
        backgroundColor: alpha(color, 0.08),
      },
    },
    card: {
      borderRadius: theme.spacing(1),
      border: `2px solid ${theme.palette.grey[200]}`,
      padding: theme.spacing(1.5),
      margin: theme.spacing(0.5, 0),
      '&:hover': {
        borderColor: alpha(color, 0.5),
        boxShadow: `0 2px 8px ${alpha(color, 0.1)}`,
      },
      '&.Mui-checked': {
        borderColor: color,
        boxShadow: `0 2px 12px ${alpha(color, 0.2)}`,
      },
    },
  };

  return {
    ...baseStyles,
    ...variantStyles[radioVariant || 'default'],
  };
});

const RadioContainer = styled(Box)<{ orientation?: 'horizontal' | 'vertical' }>(
  ({ orientation }) => ({
    display: 'flex',
    flexDirection: orientation === 'horizontal' ? 'row' : 'column',
    gap: 8,
    flexWrap: orientation === 'horizontal' ? 'wrap' : 'nowrap',
  })
);

const OptionContainer = styled(Box)<{ variant?: RadioVariant }>(
  ({ theme, variant: radioVariant }) => ({
    display: 'flex',
    alignItems: radioVariant === 'card' ? 'flex-start' : 'center',
    gap: radioVariant === 'card' ? theme.spacing(1.5) : theme.spacing(1),
    width: '100%',
  })
);

const OptionContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const OptionDescription = styled('div')(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginTop: 2,
}));

export const Radio: React.FC<CustomRadioProps> = ({
  variant = 'default',
  size = 'medium',
  label,
  description,
  ...props
}) => {
  const radioElement = (
    <StyledRadio
      {...props}
      variant={variant}
      size={size === 'large' ? 'medium' : size} // MUI doesn't have large size
    />
  );

  if (!label && !description) {
    return radioElement;
  }

  return (
    <OptionContainer variant={variant}>
      {radioElement}
      <OptionContent>
        {label && <div>{label}</div>}
        {description && <OptionDescription>{description}</OptionDescription>}
      </OptionContent>
    </OptionContainer>
  );
};

export const RadioGroup: React.FC<CustomRadioGroupProps> = ({
  variant = 'default',
  size = 'medium',
  options,
  label,
  description,
  error = false,
  helperText,
  orientation = 'vertical',
  value,
  onChange,
  ...props
}) => {
  const displayHelperText = error ? helperText : description || helperText;

  return (
    <FormControl error={error} component="fieldset">
      {label && (
        <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
          {label}
        </FormLabel>
      )}

      <MuiRadioGroup {...props} value={value} onChange={onChange} sx={{ mt: 0 }}>
        <RadioContainer orientation={orientation}>
          {options.map(option => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              control={
                <StyledRadio
                  variant={variant}
                  size={size === 'large' ? 'medium' : size}
                  error={error}
                />
              }
              label={
                <OptionContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option.icon && option.icon}
                    <span>{option.label}</span>
                  </Box>
                  {option.description && (
                    <OptionDescription>{option.description}</OptionDescription>
                  )}
                </OptionContent>
              }
              sx={{
                margin: 0,
                alignItems: variant === 'card' ? 'flex-start' : 'center',
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                },
              }}
            />
          ))}
        </RadioContainer>
      </MuiRadioGroup>

      {displayHelperText && <FormHelperText sx={{ mt: 1 }}>{displayHelperText}</FormHelperText>}
    </FormControl>
  );
};

export default RadioGroup;
